import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutGrid, List, ChartColumn, type LucideIcon } from "lucide-react";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { useOpportunities, useTeamMembers } from "@/hooks/use-api";
import { ListPage } from "@/components/v2/list-page";
import { DataTable } from "@/components/v2/data-table";
import { EmptyState } from "@/components/v2/empty-state";
import { ViewSwitcher } from "@/components/v2/view-switcher";
import { OpportunityBoardView } from "@/components/v2/opportunities/opportunity-board-view";
import { OpportunityDisplayPopover } from "@/components/v2/opportunities/display-popover";
import {
  useOpportunityViewParams,
  type OpportunityView,
} from "@/components/v2/opportunities/use-opportunity-view-params";
import { useOpportunityFilters } from "@/hooks/use-opportunity-filters";
import { AggregateStrip } from "@/components/v2/aggregate-strip";
import { FilterToolbar } from "@/components/v2/filter-toolbar";
import { MultiSelectFilter } from "@/components/v2/multi-select-filter";
import { SearchHint } from "@/components/v2/search-hint";
import { StatusBadge } from "@/components/status-badge";
import { Button } from "@/components/ui/button";
import { formatCurrency, formatDate } from "@/lib/utils";
import { LIST_PAGE_VIEWS } from "@/pages/v2/_shared/list-page-views";
import { formatShowingRange } from "@/pages/v2/_shared/format-showing-range";
import { useAddDemoAccountStore } from "@/stores/add-demo-account-store";

type Row = Record<string, unknown>;

// Minimal shape consumed from the team_members_unified row for rep options.
interface TeamMemberRow {
  contact_role_id: string;
  first_name: string | null;
  last_name: string | null;
  is_active: boolean;
}

// ADV-1359: per-view glyphs (the Linear/Catalyst segmented-control look).
const VIEW_ICONS: Record<string, LucideIcon> = {
  board: LayoutGrid,
  table: List,
  analytics: ChartColumn,
};
const VIEW_OPTIONS = LIST_PAGE_VIEWS.opportunities.options.map((o) => ({
  ...o,
  icon: VIEW_ICONS[o.value],
}));

const columns: ColumnDef<Row, unknown>[] = [
  {
    accessorKey: "name",
    header: "Opportunity",
    cell: ({ row }) => {
      const name = row.original.name as string | undefined;
      const address = row.original.address as string | undefined;
      return (
        <div className="flex flex-col">
          <span className="font-medium transition-colors group-hover:text-[color:var(--color-accent)]">
            {name || "—"}
          </span>
          {address && (
            <span className="typo-label text-[color:var(--color-text-lo)] normal-case tracking-normal">
              {address}
            </span>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "proposal_value",
    header: "Amount",
    meta: { numeric: true },
    cell: ({ row }) => (
      <span className="font-medium">
        {formatCurrency(row.original.proposal_value as number)}
      </span>
    ),
  },
  {
    accessorKey: "stage",
    header: "Stage",
    cell: ({ row }) => <StatusBadge status={row.original.stage as string} />,
  },
  {
    accessorKey: "assigned_rep_name",
    header: "Sales Rep",
    cell: ({ row }) => {
      const rep = row.original.assigned_rep_name as string | undefined;
      return (
        <span className={rep ? "" : "text-[color:var(--color-text-lo)]"}>
          {rep || "—"}
        </span>
      );
    },
  },
  {
    accessorKey: "lead_date",
    header: "Created",
    cell: ({ row }) => (
      <span className="text-[color:var(--color-text-md)]">
        {formatDate(row.original.lead_date as string)}
      </span>
    ),
  },
];

export default function V2OpportunitiesPage() {
  const navigate = useNavigate();
  const { openDialog } = useAddDemoAccountStore();
  // ADV-1359/1361: view (and the Display popover's other display params) live in
  // the URL via the shared hook so they are shareable and survive reload/back —
  // the root fix for the old switcher that navigated away and lost context. The
  // default (board) is omitted from the URL to keep the canonical path clean.
  const { view, setView } = useOpportunityViewParams();
  // ADV-1362: salesperson facet (server-side, shared with the board fetch).
  const filters = useOpportunityFilters();
  const [sorting, setSorting] = useState<SortingState>([
    { id: "lead_date", desc: true },
  ]);
  const [pageIndex, setPageIndex] = useState(0);
  const pageSize = 50;

  const { data, isLoading, isError, refetch } = useOpportunities({
    offset: pageIndex * pageSize,
    limit: pageSize,
    sort_by: sorting[0]?.id,
    sort_order: sorting[0]?.desc ? "desc" : "asc",
    ...filters.params,
  });

  // Rep options for the facet: assigned reps are team members, and an
  // opportunity's `assigned_rep_id` IS the rep's `contact_role_id` — so the
  // option ids match the filter column exactly (spec §4/D11).
  const { data: teamData } = useTeamMembers({ limit: 100 });
  const repOptions = useMemo(
    () =>
      ((teamData?.team_members ?? []) as TeamMemberRow[])
        .filter((m) => m.is_active && m.contact_role_id)
        .map((m) => ({
          id: m.contact_role_id,
          name:
            [m.first_name, m.last_name].filter(Boolean).join(" ") ||
            m.contact_role_id,
        })),
    [teamData?.team_members],
  );

  const opportunities = useMemo(
    () => (data?.opportunities ?? []) as Row[],
    [data?.opportunities],
  );
  const total = data?.total ?? 0;

  // TODO(ADV-787-followup): pipeline/avg/win-rate need a DB-side aggregate
  // view (sales.v_opportunities_aggregate) — see plan Phase 2 + agents.md
  // database-first-calculations rule. Until that view ships we render `—`
  // placeholders rather than introducing a client-side .reduce() violation.
  const aggregateItems = [
    {
      label: "Showing",
      value: formatShowingRange(
        opportunities.length,
        pageIndex,
        pageSize,
        total,
      ),
    },
    { label: "Pipeline", value: "—" },
    { label: "Avg value", value: "—" },
    { label: "Win rate", value: "—" },
  ];

  return (
    <ListPage
      title="Opportunities"
      actions={
        <>
          <SearchHint />
          {/* The Display popover is the board's control surface (spec §3); it
              rides alongside the board view only. */}
          {view === "board" && <OpportunityDisplayPopover />}
          <Button onClick={() => navigate("/opportunities/new")}>
            Create opportunity
          </Button>
        </>
      }
      tabs={
        <ViewSwitcher
          value={view}
          onValueChange={(next) => setView(next as OpportunityView)}
          options={VIEW_OPTIONS}
          aria-label="Opportunities view"
        />
      }
      filters={
        <FilterToolbar entity="opportunities">
          <MultiSelectFilter
            label="Rep"
            options={repOptions}
            selected={filters.repIds}
            onChange={(ids) => {
              // Reset to the first page: a narrower server-side result set can
              // have fewer pages than the current offset (Codex P2 — otherwise
              // changing the facet on page 3 requests an out-of-range offset and
              // renders an empty table despite matches existing).
              filters.setRepIds(ids);
              setPageIndex(0);
            }}
          />
        </FilterToolbar>
      }
      aggregateStrip={
        view === "table" ? (
          <AggregateStrip
            items={aggregateItems}
            sort={{ label: "Created", direction: "desc" }}
          />
        ) : undefined
      }
    >
      {/* ADV-1359: the real pipeline board now renders IN PLACE (no route hop). */}
      {view === "board" && <OpportunityBoardView />}
      {view === "table" && (
        <DataTable
          columns={columns}
          data={opportunities}
          total={total}
          isLoading={isLoading}
          isError={isError}
          errorEntityLabel="opportunities"
          onRetry={() => refetch()}
          onRowClick={(row) => navigate(`/opportunities/${row.id}`)}
          emptyMessage="No open opportunities"
          emptyDescription="Opportunities appear here once a lead is qualified. Create the first one to start tracking deal value."
          pageSize={pageSize}
          pageIndex={pageIndex}
          onPaginationChange={setPageIndex}
          sorting={sorting}
          onSortingChange={setSorting}
        />
      )}
      {view === "analytics" && (
        <EmptyState
          title="Analytics ships in Wave 2"
          description="The Opportunities analytics surface is part of the broader operational-screens plan."
          secondaryAction={{
            label: "Try Adva with sample data",
            onClick: openDialog,
          }}
        />
      )}
    </ListPage>
  );
}
